import React, { useEffect, useRef, useState } from "react";
import {
  MultiStepContainer,
  MultiStep,
  UCLColorKey,
  LoadingV2,
  MultiStepContainerModule,
} from "@ucl/library";
import "./HomePage.scss";
import { useDisclosures } from "@components/Disclosures/useDisclosures";
import { Step } from "@ucl/library";
import { DisclosureStep, DisclosureStepProps } from "@/components/Disclosures/DisclosureStep";
import {
  SelectProductFormStep,
  SelectProductFormStepProps,
} from "@/components/SelectProductForm/SelectProductFormStep";
import {
  BasicInformationFormStep,
  BasicInformationFormStepProps,
} from "@/components/BasicInformation/BasicInformationFormStep";
import {
  AccountDetailsFormStep,
  AccountDetailsFormStepProps,
} from "@/components/AccountDetails/AccountDetailsFormStep";
import { DisclosureStepSidebar } from "@/components/Disclosures/DisclosureStepSidebar";

import { SelectProductFormStepSidebar } from "@/components/SelectProductForm/SelectProductFormStepSidebar";
import {
  PlaidIdentityVerificationStep,
  PlaidIdentityVerificationStepProps,
} from "@/components/PlaidIdentityVerification";
import { faShoppingCart, faFileLines, faCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  JointAccountOwnersStep,
  JointAccountOwnersStepProps,
} from "@/components/JointAccountOwners/JointAccountOwnersStep";
import {
  PersonalAccountBeneficiariesStep,
  PersonalAccountBeneficiariesStepProps,
} from "@/components/PersonalAccountBeneficiaries/PersonalAccountBeneficiariesStep";
import { useFinancialApplication } from "@/hooks/useFinancialApplication";
import { KeyPeopleStep, KeyPeopleStepProps } from "@/components/KeyPeople/KeyPeopleStep";
import {
  PersonalAccountAcceptableUseFormStep,
  PersonalAccountAcceptableUseFormStepProps,
} from "@/components/PersonalAccountAcceptableUse/PersonalAccountAcceptableUseFormStep";
import {
  OrganizationAcceptableUseFormStep,
  OrganizationAcceptableUseFormStepProps,
} from "@/components/OrganizationAcceptableUse/OrganizationAcceptableUseFormStep";
import {
  OrganizationInfoFormStep,
  OrganizationInfoFormStepProps,
} from "@/components/OrganizationInfo/OrganizationInfoFormStep";
import { OptInsFormStep, OptInsFormStepProps } from "@/components/OptIns/OptInsFormStep";
import { useAtomValue } from "jotai";
import { isDirtyAtom, selectedProductDetailsAtom } from "@/atoms";
import { areAnySavesInFlightAtom } from "@/atoms/savingIndicatorAtom";
import { hasVisitedConfirmDetailsAtom } from "@/atoms";
import { AccountSignupStep, AccountSignupStepProps } from "@/components/AccountSignup";
import {
  ApplicantSummaryStep,
  ApplicantSummaryStepProps,
} from "@/components/ApplicantSummary/ApplicantSummaryStep";
import { ApplicantSummarySidebar } from "@/components/ApplicantSummary/ApplicantSummarySidebar";
import { useFinancialApplicationCoreData } from "@/hooks/useFinancialApplicationCoreData";
import { useAttachUser } from "@/hooks/useAttachUser";
import { useAuth0 } from "@auth0/auth0-react";
import { STEP_PATHS, SUBSTEP_IDS, StepPaths } from "@/config/stepPaths";
import {
  UserAttachmentBufferStep,
  UserAttachmentBufferStepProps,
} from "@/components/UserAttachmentBuffer/UserAttachmentBufferStep";
import {
  FinancialApplicationDocumentsFormStep,
  FinancialApplicationDocumentsFormStepProps,
} from "@/components/FinancialApplicationDocuments/FinancialApplicationDocumentsFormStep";
import {
  CreditAuthorizationStep,
  CreditAuthorizationStepProps,
} from "@/components/CreditAuthorizationStep/CreditAuthorizationStep";

const color: UCLColorKey = "forest-green";

interface HomePageProps {
  financialApplicationId: string;
}

export const HomePageWrapper: React.FC = () => {
  const { ensureApplication } = useFinancialApplication();
  const [financialApplicationId, setFinancialApplicationId] = useState<string | undefined>(
    undefined
  );
  useEffect(() => {
    const autoCreate = true;
    ensureApplication(autoCreate).then((id) => {
      setFinancialApplicationId(id);
    });
  }, []);

  if (!financialApplicationId) return <LoadingV2 loadingType="spinner" height="100vh" />;

  return <HomePage financialApplicationId={financialApplicationId!} />;
};

const HomePage: React.FC<HomePageProps> = ({ financialApplicationId }) => {
  const { coreData, conditionalFlags } = useFinancialApplicationCoreData(financialApplicationId);
  const { disclosures } = useDisclosures(financialApplicationId);
  const { isAttached } = useAttachUser(financialApplicationId);
  const { isAuthenticated } = useAuth0();
  const isDirty = useAtomValue(isDirtyAtom);
  const isSaving = useAtomValue(areAnySavesInFlightAtom);
  const hasVisitedConfirmDetails = useAtomValue(hasVisitedConfirmDetailsAtom);
  const selectedProductDetails = useAtomValue(selectedProductDetailsAtom);
  const multiStepContainerRef = useRef<MultiStepContainerModule>(null);

  // Because we need to know what disclosures are available to make the steps. - This should be changed.
  if (!disclosures || !coreData) return <LoadingV2 loadingType="spinner" height="100vh" />;

  const steps: MultiStep[] = [
    {
      id: STEP_PATHS.SelectProduct,
      label: "Select Product",
      props: {
        financialApplicationId: financialApplicationId,
        progressBarReplacementContent: (
          <h3 className="selected-products-step-progress-bar-replacement-content">
            Please select one or more products to continue.
          </h3>
        ),
      },
      component: SelectProductFormStep,
      sideBar: {
        title: (
          <>
            <FontAwesomeIcon icon={faShoppingCart} className="selected-products-sidebar__icon" />
            <span className="selected-products-sidebar__title">
              Selected Products
              {selectedProductDetails.length > 0 && (
                <span className="selected-products-sidebar__count">
                  {selectedProductDetails.length} <FontAwesomeIcon icon={faCheck} />
                </span>
              )}
            </span>
          </>
        ),
        content: <SelectProductFormStepSidebar />,
      },
      nextButtonLabel: `Continue with ${selectedProductDetails.length} Product(s)`,
      nextButtonSettings: {
        isDisabled: selectedProductDetails.length === 0,
        isDisabledTooltipContent:
          "You have not yet selected any products. Please select one or more products to continue.",
      },
    } as Step<SelectProductFormStepProps>,
    {
      id: STEP_PATHS.Verification,
      label: "Verification",
      substeps: disclosures.map(
        (disclosure) =>
          ({
            id: SUBSTEP_IDS.Verification.getDisclosureId(disclosure.slug),
            label: disclosure.label,
            props: {
              disclosureId: disclosure.id,
              financialApplicationId: financialApplicationId,
            },
            component: DisclosureStep,
            sideBar: {
              title: (
                <>
                  <FontAwesomeIcon icon={faFileLines} className="all-documents-sidebar__icon" />
                  <span className="all-documents-sidebar__title">All Documents</span>
                </>
              ),
              content: <DisclosureStepSidebar financialApplicationId={financialApplicationId} />,
            },
            nextButtonLabel: "Agree and Continue",
          }) as Step<DisclosureStepProps>
      ),
      available: disclosures.length > 0,
    },
    {
      id: STEP_PATHS.AboutYou,
      label: "About You",
      substeps: [
        {
          id: SUBSTEP_IDS.AboutYou.AccountSignup,
          label: "Create Account",
          available: !isAuthenticated,
          props: {
            financialApplicationId: financialApplicationId,
            returnToUrl: `${window.location.origin}${StepPaths.buildStepUrl(STEP_PATHS.AboutYou, SUBSTEP_IDS.AboutYou.PreparingYourApplication)}`,
          },
          component: AccountSignupStep,
          nextButtonLabel: "Continue",
        } as Step<AccountSignupStepProps>,
        {
          id: SUBSTEP_IDS.AboutYou.PreparingYourApplication,
          label: "Preparing Your Application",
          available: isAuthenticated && isAttached !== true, // Only show for authenticated users
          props: {
            financialApplicationId: financialApplicationId,
          },
          component: UserAttachmentBufferStep,
          nextButtonLabel: "Continue",
        } as Step<UserAttachmentBufferStepProps>,
        {
          id: SUBSTEP_IDS.AboutYou.IdentityVerification,
          label: "Identity Verification",
          available: coreData.shouldRunIdvFlow,
          props: {
            financialApplicationId: financialApplicationId,
          },
          component: PlaidIdentityVerificationStep,
          nextButtonLabel: "Continue",
        } as Step<PlaidIdentityVerificationStepProps>,
        {
          id: SUBSTEP_IDS.AboutYou.BasicInfo,
          label: "Basic Info",
          props: {
            financialApplicationId: financialApplicationId,
          },
          component: BasicInformationFormStep,
        } as Step<BasicInformationFormStepProps>,
        {
          id: SUBSTEP_IDS.AboutYou.AccountDetails,
          label: "Account Details",
          props: {
            financialApplicationId: financialApplicationId,
          },
          component: AccountDetailsFormStep,
        } as Step<AccountDetailsFormStepProps>,
      ],
    },
    {
      id: STEP_PATHS.JointOwners,
      label: "Joint Owners",
      available: conditionalFlags.isJointAccountOwnersEnabled,
      props: {
        financialApplicationId: financialApplicationId,
      },
      component: JointAccountOwnersStep,
    } as Step<JointAccountOwnersStepProps>,
    {
      id: STEP_PATHS.Beneficiaries,
      label: "Beneficiaries",
      available: conditionalFlags.isPersonalAccountBeneficiariesEnabled,
      props: {
        financialApplicationId: financialApplicationId,
      },
      component: PersonalAccountBeneficiariesStep,
    } as Step<PersonalAccountBeneficiariesStepProps>,
    {
      id: STEP_PATHS.EntityInfo,
      label: "Entity Info",
      available: conditionalFlags.isOrganizationInfoEnabled,
      substeps: [
        {
          id: SUBSTEP_IDS.EntityInfo.EntityBasicInfo,
          label: "Entity Information",
          available: conditionalFlags.isOrganizationInfoEnabled,
          props: {
            financialApplicationId: financialApplicationId,
          },
          component: OrganizationInfoFormStep,
        } as Step<OrganizationInfoFormStepProps>,
        {
          id: SUBSTEP_IDS.EntityInfo.KeyPeople,
          label: "Key People",
          description:
            "We'll ask a few questions about your business to help us better understand your needs.",
          available: conditionalFlags.isKeyPeopleEnabled,
          props: {
            financialApplicationId: financialApplicationId,
          },
          component: KeyPeopleStep,
        } as Step<KeyPeopleStepProps>,
      ],
    },
    {
      id: STEP_PATHS.AdditionalInfo,
      label: "Additional Info",
      substeps: [
        {
          id: SUBSTEP_IDS.AdditionalInfo.PersonalAccountAcceptableUse,
          label: "Personal Account Acceptable Use",
          available: conditionalFlags.isPersonalAccountAcceptableUseEnabled,
          props: {
            financialApplicationId: financialApplicationId,
          },
          component: PersonalAccountAcceptableUseFormStep,
        } as Step<PersonalAccountAcceptableUseFormStepProps>,
        {
          id: SUBSTEP_IDS.AdditionalInfo.OrganizationAcceptableUse,
          label: "Entity Acceptable Use",
          available: conditionalFlags.isOrganizationAcceptableUseEnabled,
          props: {
            financialApplicationId: financialApplicationId,
          },
          component: OrganizationAcceptableUseFormStep,
        } as Step<OrganizationAcceptableUseFormStepProps>,
        {
          id: SUBSTEP_IDS.AdditionalInfo.CreditAuthorization,
          label: "Credit Authorization",
          available: coreData.hasCreditAccountSelected,
          props: {
            creditAuthorizationId: coreData.applicantCreditAuthorizationId,
          },
          component: CreditAuthorizationStep,
        } as Step<CreditAuthorizationStepProps>,
        {
          id: SUBSTEP_IDS.AdditionalInfo.Preferences,
          label: "Preferences",
          props: {
            financialApplicationId: financialApplicationId,
          },
          component: OptInsFormStep,
        } as Step<OptInsFormStepProps>,
        {
          id: SUBSTEP_IDS.AdditionalInfo.ApplicationDocuments,
          label: "Application Documents",
          props: {
            financialApplicationId: financialApplicationId,
          },
          component: FinancialApplicationDocumentsFormStep,
        } as Step<FinancialApplicationDocumentsFormStepProps>,
      ],
    },
    {
      id: STEP_PATHS.ConfirmDetails,
      label: "Confirm Details",
      nextButtonLabel: "Submit Application",
      props: {
        financialApplicationId: financialApplicationId,
      },
      component: ApplicantSummaryStep,
      nextButtonSettings: {
        showLoading: true,
      },
      sideBar: {
        title: (
          <>
            <FontAwesomeIcon icon={faFileLines} className="review-application-sidebar__icon" />
            <span className="review-application-sidebar__title">Review Your Application</span>
          </>
        ),
        content: <ApplicantSummarySidebar financialApplicationId={financialApplicationId} />,
      },
    } as Step<ApplicantSummaryStepProps>,
  ];

  return (
    <div className="home-page">
      <div className="home-page__container">
        <MultiStepContainer
          steps={steps}
          defaultStep={STEP_PATHS.SelectProduct}
          color={color}
          backButtonLabel="Go Back"
          nextButtonLabel="Continue"
          width="75%"
          height="88%"
          nextButtonSettings={{
            isDirty: isDirty || isSaving,
          }}
          ref={multiStepContainerRef}
          quickNavigate={{
            label: "Back to Confirm Details",
            targetStepId: STEP_PATHS.ConfirmDetails,
            shouldShow: hasVisitedConfirmDetails,
          }}
        />
      </div>
    </div>
  );
};

export default HomePage;
